import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Col, Row, Spin } from "antd";
import PercentageCompletionGraph from "./percentageCompletionGraph";
import { fetchProjectCompletionData } from "../redux/actions";
import "./graphSection.css";

// Define workflow stages for consistent ordering
const MATERIAL_WORKFLOW_STAGES = [
  "fabrication",
  "qc_release",
  "corrosion_protection",
  "delivery_to_site",
  "site_receipt",
  "installation"
];

const BOLTS_WORKFLOW_STAGES = [
  "fabrication",
  "qc_release",
  "delivery_to_workshop",
  "delivery_to_site",
  "site_receipt"
];

// Stage display names mapping
const STAGE_DISPLAY_NAMES = {
  fabrication: "Fabrication",
  qc_release: "QC Release",
  corrosion_protection: "Corrosion \n Protection",
  delivery_to_site: "Delivery to Site",
  site_receipt: "Site Receipt",
  installation: "Installation",
  delivery_to_workshop: "Delivery to Workshop"
};

const ActivitiesReporting = ({ project_id, searchResultIds = [] }) => {
  const dispatch = useDispatch();
  const [materialsData, setMaterialsData] = useState([]);
  const [boltsData, setBoltsData] = useState([]);
  // Get project ID and completion data from Redux store
  const { completionData, fetchingCompletionData } = useSelector((state) => state.projectReducer);

  /**
   * Transform API completion stats data into the format expected by the graph component
   * @param {Object} completionStats - The completion stats from the API
   * @param {String} type - Either 'material' or 'bolts'
   * @returns {Array} - Formatted data for the graph
   */
  const transformCompletionData = (completionStats, type) => {
    if (!completionStats) return [];
    
    // Select the appropriate workflow stages based on type
    const stages = type === 'material' ? MATERIAL_WORKFLOW_STAGES : BOLTS_WORKFLOW_STAGES;
    
    return stages
      .filter(stage => completionStats[stage]) // Only include stages that exist in the data
      .map(stage => ({
        label: STAGE_DISPLAY_NAMES[stage],
        value: Math.round(completionStats[stage].percentage) // Round to nearest integer
      }));
  };

  /**
   * Process completion data from API
   */
  useEffect(() => {
    if (completionData) {
      // Transform API data into the format expected by the graph component
      if (completionData.material && completionData.material.completionStats) {
        const transformedMaterialData = transformCompletionData(completionData.material.completionStats, 'material');
        setMaterialsData(transformedMaterialData);
      }
      
      if (completionData.bolts && completionData.bolts.completionStats) {
        const transformedBoltsData = transformCompletionData(completionData.bolts.completionStats, 'bolts');
        setBoltsData(transformedBoltsData);
      }
    }
  }, [completionData]);
  
  /**
   * Fetch completion data when project ID or search result IDs change
   */
  useEffect(() => {
    if (project_id) {
      // Dispatch the action with the project ID and search result IDs
      dispatch(fetchProjectCompletionData({ project_id }, searchResultIds));
    }
  }, [project_id, JSON.stringify(searchResultIds), dispatch]);


  return (
    <Row className="doc-control-parent-row">
      {/* Header */}
      <Col span={24} className="heading-col">
        <Row>
          <Col>
            <span className="heading-span material-control-reporting">MATERIAL CONTROL REPORTING</span>
          </Col>
        </Row>
      </Col>
      
      {/* Graph section */}
      <Col span={24} className="graph-section">
      {fetchingCompletionData ? (
          <div className="spinner-div">
            <Spin size="normal" />
          </div>
        ) : (
        <Row gutter={[20, 40]}>
          {/* Materials graph */}
          <Col md={12} lg={12} xl={12} xxl={12}>
            <div className="graph-title-div">
              <span className="graph-text-span">MATERIALS</span>
            </div>

            <PercentageCompletionGraph data={materialsData} />
          </Col>
          
          {/* Bolts graph */}
          <Col md={12} lg={12} xl={12} xxl={12}>
            <div className="graph-title-div">
              <span className="graph-text-span">BOLTS</span>
            </div>
            <PercentageCompletionGraph data={boltsData} />
          </Col>
        </Row>
        )}
      </Col>
    </Row>
  );
};

export default ActivitiesReporting;
